<template>
  <div class="profile-container">
    <div class="profile-header">
      <h2>{{ $t('profile.title') }}</h2>
      <button @click="handleLogout" class="logout-btn">{{ $t('profile.logout') }}</button>
    </div>

    <div class="profile-content">

      <!-- 用户信息 -->
      <section class="section">
        <div class="section-header">
          <svg class="section-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          <h3>{{ $t('profile.user_info') }}</h3>
        </div>
        <div class="section-body">
          <div class="info-grid">
            <div class="info-row">
              <span class="info-label">ID</span>
              <span class="info-value">{{ userInfo.userId }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('profile.username') }}</span>
              <span class="info-value">{{ userInfo.username }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('profile.email') }}</span>
              <span class="info-value">{{ userInfo.email }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('profile.company') }}</span>
              <span class="info-value">{{ userInfo.company }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- 许可证信息 -->
      <section class="section">
        <div class="section-header">
          <svg class="section-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          <h3>{{ $t('profile.license_info') }}</h3>
        </div>
        <div class="section-body">
          <div v-if="licenseInfo" class="info-grid">
            <div class="info-row">
              <span class="info-label">{{ $t('profile.license_type') }}</span>
              <span class="info-value">
                <span class="tier-badge" :class="'tier-' + (licenseInfo.productType || 'community')">{{ licenseTypeLabel }}</span>
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('profile.status') }}</span>
              <span class="info-value" :class="licenseInfo.status === 'active' ? 'text-active' : 'text-inactive'">
                {{ licenseInfo.status === 'active' ? $t('profile.active') : $t('profile.inactive') }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('profile.expiry_date') }}</span>
              <span class="info-value">{{ formatDate(licenseInfo.expiryDate) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('profile.devices') }}</span>
              <span class="info-value">{{ licenseInfo.currentDevices }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('profile.license_key') }}</span>
              <span class="info-value key-value">
                <span class="key-text" :class="{ 'key-hidden': !showLicenseKey }">
                  {{ showLicenseKey ? licenseInfo.licenseKey : '••••••••••••••••••••••••••••••' }}
                </span>
                <button @click="toggleLicenseKey" class="icon-btn" :title="showLicenseKey ? $t('profile.hide_key') : $t('profile.show_key')">
                  <svg v-if="showLicenseKey" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg v-else viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
                <button @click="copyLicenseKey" class="icon-btn">{{ $t('profile.copy_key') }}</button>
              </span>
            </div>
            <div class="info-row" v-if="features.length">
              <span class="info-label">{{ $t('profile.features') }}</span>
              <span class="info-value tags-row">
                <span class="feat-tag" v-for="f in features" :key="f">{{ featLabel(f) }}</span>
              </span>
            </div>
          </div>
          <div v-else class="info-grid">
            <div class="info-row">
              <span class="info-label">{{ $t('profile.license_type') }}</span>
              <span class="info-value">{{ $t('profile.unknown') }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('profile.status') }}</span>
              <span class="info-value">{{ $t('profile.unknown') }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('profile.expiry_date') }}</span>
              <span class="info-value">{{ $t('profile.unknown') }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('profile.devices') }}</span>
              <span class="info-value">{{ $t('profile.unknown') }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('profile.license_key') }}</span>
              <span class="info-value">{{ $t('profile.unknown') }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- 用量与限制 -->
      <section class="section" v-if="usageInfo">
        <div class="section-header">
          <svg class="section-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/></svg>
          <h3>{{ $t('profile.usage_info') }}</h3>
        </div>
        <div class="section-body">
          <div class="quota-grid">
            <div class="quota-card" v-for="q in quotaItems" :key="q.key">
              <div class="quota-head">
                <span class="quota-name">{{ q.label }}</span>
                <span v-if="isUnlimited(q.key)" class="quota-val">{{ $t('profile.usage_unlimited') }}</span>
                <span v-else class="quota-val">{{ (usageInfo && usageInfo[q.key]) || 0 }}{{ $t('profile.usage_times') }}{{ $t('profile.usage_of') }}{{ (quotaInfo && quotaInfo[q.key]) || 0 }}</span>
              </div>
              <div class="bar-bg" v-if="!isUnlimited(q.key)">
                <div class="bar-fill" :style="{ width: usagePercent(q.key) + '%' }" :class="barClass(q.key)"></div>
              </div>
            </div>
          </div>

          <div class="limit-grid" v-if="limitInfo">
            <div class="limit-item">
              <span>🤖</span>
              <div class="limit-body">
                <span class="limit-name">{{ $t('profile.usage_agent_limit') }}</span>
                <span class="limit-val">{{ formatLimit(limitInfo.agentLimit) }}</span>
              </div>
            </div>
            <div class="limit-item">
              <span>📚</span>
              <div class="limit-body">
                <span class="limit-name">{{ $t('profile.usage_kb_limit') }}</span>
                <span class="limit-val">{{ formatLimit(limitInfo.kbLimit) }}</span>
              </div>
            </div>
            <div class="limit-item">
              <span>📄</span>
              <div class="limit-body">
                <span class="limit-name">{{ $t('profile.usage_doc_limit') }}</span>
                <span class="limit-val">{{ formatLimit(limitInfo.docPerKbLimit) }} {{ $t('profile.usage_doc_unit') }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 操作 -->
      <div class="actions">
        <button v-if="licenseInfo && licenseInfo.productType === 'community'" class="upgrade-btn">
          {{ $t('profile.upgrade_to_enterprise') }}
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { encryptUserData, decryptUserData } from '../utils/web-crypto';
import ModalService from '../ui_server/modalService';

export default {
  name: 'Profile',
  data() {
    return {
      userInfo: {
        username: '',
        email: '',
        company: ''
      },
      licenseInfo: null,
      showLicenseKey: false,
      usageInfo: null,
      quotaInfo: null,
      features: [],
      limitInfo: null
    }
  },
  computed: {
    licenseTypeLabel() {
      const map = {
        'community': this.$t('profile.license_community') || '社区版',
        'basic': this.$t('profile.license_basic') || '基础版',
        'pro': this.$t('profile.license_pro') || '专业版',
        'enterprise': this.$t('profile.license_enterprise') || '企业版',
      };
      return map[this.licenseInfo?.productType] || this.licenseInfo?.productType || '';
    },
    quotaItems() {
      return [
        { key: 'imageGen',       label: this.$t('profile.usage_image_gen') },
        { key: 'speechAsr',      label: this.$t('profile.usage_speech_asr') },
        { key: 'videoAnalyze',   label: this.$t('profile.usage_video_analyze') },
        { key: 'reportGen',      label: this.$t('profile.usage_report_gen') },
        { key: 'mindmapGen',     label: this.$t('profile.usage_mindmap_gen') },
        { key: 'cardsGen',       label: this.$t('profile.usage_cards_gen') },
        { key: 'quizGen',        label: this.$t('profile.usage_quiz_gen') },
        { key: 'infographicGen', label: this.$t('profile.usage_infographic_gen') },
        { key: 'pptGen',         label: this.$t('profile.usage_ppt_gen') },
        { key: 'podcastGen',     label: this.$t('profile.usage_podcast_gen') },
        { key: 'wikiGen',        label: this.$t('profile.usage_wiki_gen') },
        { key: 'videoGen',       label: this.$t('profile.usage_video_gen') },
      ];
    }
  },
  mounted() {
    this.loadUserProfile()
  },
  methods: {
    async loadUserProfile() {
      try {
        const token = localStorage.getItem('token')
        // console.log('从localStorage获取的令牌:', token)
        if (!token) {
          this.$router.push('/login')
          return
        }
        
        // 获取用户信息
        const userResponse = await axios.get('/api/user', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-client-id': 'web'
          }
        })
        if (userResponse.data.success) {
          // 解密用户信息
          const decryptedData = await decryptUserData(userResponse.data.encryptedData);
          
          this.userInfo = {
            userId: decryptedData.u,
            username: decryptedData.un,
            email: decryptedData.e,
            company: decryptedData.c
          };
        } else {
          // 如果获取用户信息失败，跳转到登录页
          this.$router.push('/login')
        }
        
        // 获取许可证信息
        // 从localStorage中获取用户信息，其中包含许可证密钥
        const userInfoStr = localStorage.getItem('userInfo')
        if (userInfoStr) {
          let userInfo = null
          try {
            userInfo = JSON.parse(userInfoStr)
          } catch (parseError) {
            console.error('解析用户信息失败:', parseError)
          }
          
          // 从用户信息中获取许可证密钥
          const licenseKey = userInfo.l || localStorage.getItem('licenseKey')
          if (licenseKey) {
            // 准备许可证验证数据
            const licenseData = {
              lk: licenseKey
            };
            
            // 加密许可证数据
            const encryptedLicenseData = await encryptUserData(licenseData);
            
            // 从后端API获取真实的许可证信息
            const licenseResponse = await axios.post('/api/license/validate-license', { 
              encryptedData: encryptedLicenseData 
            }, {
              headers: {
                'x-client-id': 'web'
              }
            })
            
            // 即使许可证未激活也显示许可证信息
            if (licenseResponse.data.valid || licenseResponse.data.encryptedData) {
              // 解密许可证信息
              const decryptedLicenseData = await decryptUserData(licenseResponse.data.encryptedData);
              
              this.licenseInfo = {
                productType: decryptedLicenseData.productType || 'unknown',
                status: decryptedLicenseData.status || 'unknown',
                expiryDate: decryptedLicenseData.expiryDate || null,
                currentDevices: decryptedLicenseData.currentDevices || 0,
                maxDevices: decryptedLicenseData.maxDevices || 0,
                licenseKey: licenseKey
              }
            } else if (licenseResponse.data.message) {
              // 显示错误信息
              console.error('许可证验证失败:', licenseResponse.data.message)
            }
          }
        }

        // 获取用量信息（含许可证状态，需要解密）
        const statusResponse = await axios.get('/api/license/status', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-client-id': 'web'
          }
        })
        if (statusResponse.data.encryptedData) {
          const decrypted = await decryptUserData(statusResponse.data.encryptedData)
          this.quotaInfo = decrypted.quotas || null
          this.usageInfo = decrypted.usage || null
          this.features = decrypted.features || []
          this.limitInfo = decrypted.limits || null
        }
      } catch (error) {
        console.error('加载用户信息失败:', error)
        // 清除本地存储并跳转到登录页
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        localStorage.removeItem('licenseKey')
        this.$router.push('/login')
      }
    },
    handleLogout() {
      // 清除本地存储的用户信息和token
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
      localStorage.removeItem('licenseKey')
      
      // 跳转到首页
      this.$router.push('/')
    },
    async upgradeLicense() {
      try {
        const userInfoStr = localStorage.getItem('userInfo')
        if (!userInfoStr) {
          ModalService.showError('无法获取用户信息', '');
          return
        }
        
        let userInfo = null
        try {
          userInfo = JSON.parse(userInfoStr)
        } catch (parseError) {
          console.error('解析用户信息失败:', parseError)
          ModalService.showError('无法获取用户信息', '');
          return
        }
        
        // 获取许可证密钥
        const licenseKey = userInfo.l || localStorage.getItem('licenseKey')
        if (!licenseKey) {
          ModalService.showError('无法获取许可证信息', '');
          return
        }
        
        // 准备许可证升级数据
        const upgradeData = {
          lk: licenseKey,
          pt: 'enterprise'
        };
        
        // 加密许可证升级数据
        const encryptedUpgradeData = await encryptUserData(upgradeData);
        
        const response = await axios.post('/api/license/upgrade-license', { 
          encryptedData: encryptedUpgradeData 
        }, {
          headers: {
            'x-client-id': 'web'
          }
        })
        
        if (response.data.success) {
          ModalService.showToast('升级成功！', 3000);
          // 更新本地存储的许可证信息
          if (response.data.encryptedData) {
            // 解密响应数据
            const decryptedData = await decryptUserData(response.data.encryptedData);
            const licenseInfo = decryptedData.li;
            
            // 更新许可证信息
            this.licenseInfo = {
              productType: licenseInfo.productType,
              status: licenseInfo.status,
              expiryDate: licenseInfo.expiryDate,
              currentDevices: licenseInfo.currentDevices,
              maxDevices: licenseInfo.maxDevices,
              licenseKey: licenseKey
            }
          } else {
            // 如果响应中没有许可证信息，重新加载用户信息
            this.loadUserProfile()
          }
        } else {
          ModalService.showError('升级失败：', response.data.message);
        }
      } catch (error) {
        console.error('升级失败:', error)
        ModalService.showError('升级失败，请稍后重试', '');
      }
    },
    formatDate(dateString) {
      if (!dateString) return '未知'
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN')
    },
    toggleLicenseKey() {
      this.showLicenseKey = !this.showLicenseKey
    },
    copyLicenseKey() {
      if (this.licenseInfo && this.licenseInfo.licenseKey) {
        navigator.clipboard.writeText(this.licenseInfo.licenseKey)
          .then(() => {
            // 可以添加一个临时提示，比如显示"已复制到剪切板"
            ModalService.showSuccess('许可证密钥已复制到剪切板', '');
          })
          .catch(err => {
            console.error('复制失败:', err)
            ModalService.showError('复制失败，请手动复制', '');
          })
      }
    },
    isUnlimited(key) {
      return this.quotaInfo && this.quotaInfo[key] >= 9999
    },
    usagePercent(key) {
      if (!this.quotaInfo || !this.usageInfo) return 0
      const quota = this.quotaInfo[key]
      const used = this.usageInfo[key] || 0
      if (!quota || quota >= 9999) return 0
      return Math.min(Math.round((used / quota) * 100), 100)
    },
    barClass(key) {
      const pct = this.usagePercent(key)
      if (pct >= 90) return 'bar-danger'
      if (pct >= 70) return 'bar-warn'
      return 'bar-ok'
    },
    featLabel(key) {
      const m = {
        basic_note: 'feat_basic_note',
        rag_basic: 'feat_rag_basic',
        rag_medium: 'feat_rag_medium',
        rag_pro: 'feat_rag_pro',
        image_gen: 'feat_image_gen',
        speech_asr: 'feat_speech_asr',
        agent_basic: 'feat_agent_basic',
        agent_medium: 'feat_agent_medium',
        agent_pro: 'feat_agent_pro',
        premium_support: 'feat_premium_support',
        team_collab: 'feat_team_collab',
        shared_kb: 'feat_shared_kb',
        user_mgmt: 'feat_user_mgmt',
        // 9 大产出模块
        output_report: 'feat_output_report',
        output_mindmap: 'feat_output_mindmap',
        output_cards: 'feat_output_cards',
        output_quiz: 'feat_output_quiz',
        output_infographic: 'feat_output_infographic',
        output_ppt: 'feat_output_ppt',
        output_podcast: 'feat_output_podcast',
        output_wiki: 'feat_output_wiki',
        output_video: 'feat_output_video',
      }
      const i18nKey = m[key]
      return i18nKey ? this.$t('profile.' + i18nKey) : key
    },
    formatLimit(val) {
      if (val == null) return '-'
      if (val >= 9999) return this.$t('profile.usage_unlimited')
      return val + (val > 0 ? '' : '')
    }
  }
}
</script>

<style scoped>
.profile-container {
  padding: 24px;
  max-width: 820px;
  margin: 0 auto;
}

.profile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
}

.profile-header h2 {
  margin: 0;
  font-size: 22px;
  color: #1a1a2e;
}

.logout-btn {
  padding: 8px 18px;
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}

.logout-btn:hover {
  background: #fecaca;
}

.profile-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Section card */
.section {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(0,0,0,.08);
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 20px 0;
}

.section-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
}

.section-icon {
  color: #6366f1;
  flex-shrink: 0;
}

.section-body {
  padding: 14px 20px 18px;
}

/* Info grid rows */
.info-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.info-label {
  width: 100px;
  flex-shrink: 0;
  font-size: 13px;
  color: #6b7280;
  padding-top: 2px;
}

.info-value {
  flex: 1;
  font-size: 14px;
  color: #1f2937;
  word-break: break-word;
}

/* Tier badge */
.tier-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  vertical-align: middle;
}
.tier-community { background:#f3f4f6; color:#6b7280; }
.tier-basic    { background:#dbeafe; color:#2563eb; }
.tier-pro      { background:#fae8ff; color:#c026d3; }
.tier-enterprise { background:#fef3c7; color:#d97706; }

.text-active  { color:#16a34a; font-weight:500; }
.text-inactive { color:#dc2626; font-weight:500; }

/* License key */
.key-value {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.key-text {
  font-family: "SF Mono","Fira Code","Consolas",monospace;
  font-size: 13px;
  word-break: break-all;
  flex: 1;
  min-width: 0;
}
.key-text.key-hidden { color:#9ca3af; }

.icon-btn {
  padding: 4px 8px;
  font-size: 12px;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  cursor: pointer;
  background: #f9fafb;
  color: #374151;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: background .15s;
}
.icon-btn:hover { background: #e5e7eb; }

/* Feature tags */
.tags-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.feat-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  background: #eef2ff;
  color: #4f46e5;
  border: 1px solid #c7d2fe;
}

/* Quota grid (usage bars) */
.quota-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.quota-card {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px 14px;
  border: 1px solid #f0f0f0;
}

.quota-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.quota-name {
  font-size: 13px;
  color: #4b5563;
  font-weight: 500;
}

.quota-val {
  font-size: 13px;
  color: #374151;
  font-weight: 500;
}

.bar-bg {
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width .3s ease;
}

.bar-ok   { background: linear-gradient(90deg,#4ade80,#22c55e); }
.bar-warn { background: linear-gradient(90deg,#fbbf24,#f59e0b); }
.bar-danger { background: linear-gradient(90deg,#fb923c,#ef4444); }

/* Limit grid */
.limit-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #f0f0f0;
}

.limit-item {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f9fafb;
  border-radius: 8px;
  padding: 10px 12px;
  border: 1px solid #f0f0f0;
}

.limit-item > span:first-child {
  font-size: 20px;
  line-height: 1;
}

.limit-body {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.limit-name {
  font-size: 12px;
  color: #6b7280;
}

.limit-val {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

/* Actions */
.actions {
  text-align: center;
  padding: 4px 0 8px;
}

.upgrade-btn {
  padding: 10px 28px;
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: background .2s;
}
.upgrade-btn:hover { background: #fde68a; }

/* Responsive */
@media (max-width: 640px) {
  .quota-grid,
  .limit-grid {
    grid-template-columns: 1fr;
  }

  .profile-header {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }

  .info-row {
    flex-direction: column;
    gap: 2px;
  }
  .info-label { width: auto; }
}
</style>
